/** Routes
 *
 *  Oversikt                              => /
 *  Beholdning                            => /holdings
 *  Ordresøk                              => /holdings/search/orders?ticker&fromDate&toDate&type&orderId&status
 *  Transaksjoner/sluttsedler (alle)      => /holdings/search/transactions?ticker&fromDate&toDate&type&orderId&status
 *  Fiktiv portefølje                     => /fictive-portfolios
 *  Kurslister                            => /list/:pageId
 *  Søkeresultater                        => /search?query
 *  Analyser                              => /analysis
 *  Detaljsider (ulike typer)             => /instrument/:itemSector
 *  Teknisk analyse                       => /instrument/:itemSector#advanced
 *  Min profil (tjenester)                => /account/overview
 *  Nyheter (alle)                        => /news
 *  Alarmer                               => /alerts
 *  Handle                                => /orders/new?itemSector
 */
import React from 'react';
import {
  BrowserRouter as Router,
  Route,
  Redirect,
  Switch,
} from 'react-router-dom';
import PrivateRoute from 'containers/App';
import AdminRoute from 'containers/admin/App/App';
import { I18nProvider } from '@lingui/react';
import noCatalog from '@oms/components-i18n/locale/no/messages';
import moment from 'moment';
import nbLocale from 'moment/locale/nb';
const OverviewPage = React.lazy(() => import('containers/OverviewPage'));
const HoldingsPage = React.lazy(() => import('containers/HoldingsPage'));
const FictivePortfoliosPage = React.lazy(() =>
  import('containers/FictivePortfoliosPage'),
);
const ListsPage = React.lazy(() => import('containers/ListsPage'));
const AnalysisPage = React.lazy(() => import('containers/AnalysisPage'));
const InstrumentPage = React.lazy(() => import('containers/InstrumentPage'));
const TradesPage = React.lazy(() => import('containers/TradesPage'));
const NewsPage = React.lazy(() => import('containers/NewsPage'));
const TDNNewsPage = React.lazy(() => import('containers/TDNNewsPage'));
const CalendarPage = React.lazy(() => import('containers/CalendarPage'));
const OrderSearchPage = React.lazy(() => import('containers/OrderSearchPage'));
const OrderDetailsPage = React.lazy(() =>
  import('containers/OrderDetailsPage'),
);
const TransactionDetailsPage = React.lazy(() =>
  import('containers/TransactionDetailsPage'),
);
const PremiumDepotPage = React.lazy(() =>
  import('containers/PremiumDepotPage'),
);
const TransactionSearchPage = React.lazy(() =>
  import('containers/TransactionSearchPage'),
);
const TransactionPrintPage = React.lazy(() =>
  import('containers/TransactionPrintPage'),
);
const AlertsPage = React.lazy(() => import('containers/AlertsPage'));
const EditAlertsPage = React.lazy(() => import('containers/EditAlertsPage'));
const NewOrderPage = React.lazy(() => import('containers/NewOrderPage'));
const EditOrderPage = React.lazy(() => import('containers/EditOrderPage'));
const NewTransactionPage = React.lazy(() =>
  import('containers/NewTransactionPage'),
);
const AccountPage = React.lazy(() => import('containers/AccountPage'));
const FAQPage = React.lazy(() => import('containers/FAQPage'));

const NotFoundPage = React.lazy(() => import('containers/NotFoundPage'));
const LoggedOutMessagePage = React.lazy(() =>
  import('containers/LoggedOutMessagePage'),
);
const NotCustomerMessagePage = React.lazy(() =>
  import('containers/NotCustomerMessagePage'),
);

const UserListPage = React.lazy(() => import('containers/admin/UserListPage'));
const UserNewPage = React.lazy(() => import('containers/admin/UserNewPage'));
const UserEditPage = React.lazy(() => import('containers/admin/UserEditPage'));
const CustomerSearchPage = React.lazy(() =>
  import('containers/admin/CustomerSearchPage'),
);
const AuditReportPage = React.lazy(() =>
  import('containers/admin/AuditReportPage'),
);
const AuditSearchPage = React.lazy(() =>
  import('containers/admin/AuditSearchPage'),
);
const AuditStatisticsPage = React.lazy(() =>
  import('containers/admin/AuditStatisticsPage'),
);
const SnipletsPage = React.lazy(() => import('containers/admin/SnipletsPage'));
const SnipletEditPage = React.lazy(() =>
  import('containers/admin/SnipletEditPage'),
);
const CurrencyEditPage = React.lazy(() =>
  import('containers/admin/CurrencyEditPage'),
);
const ReportListPage = React.lazy(() =>
  import('containers/admin/ReportListPage'),
);
const CompanyReportEditPage = React.lazy(() =>
  import('containers/admin/CompanyReportEditPage'),
);
const FileUploadPage = React.lazy(() =>
  import('containers/admin/FileUploadPage'),
);
const AdminLoginPage = React.lazy(() => import('containers/admin/LoginPage'));
const AdminChangePassword = React.lazy(() =>
  import('containers/admin/ChangePasswordPage'),
);
const TakeoverInProgressPage = React.lazy(() =>
  import('containers/admin/TakeoverInProgress'),
);

const catalogs = {
  // Include the norwegian language pack
  no: noCatalog,
};

// Load norwegian locale
moment.updateLocale('nb', nbLocale);
moment.locale('nb');

function setPage(nextState, setState) {
  if (!nextState.location.query.page) {
    setState(null, nextState.location.pathname, { page: 1 });
  }
}

export default () => (
  <I18nProvider language="no" catalogs={catalogs}>
    <Router>
      <Switch>
        {/* Trader routes */}
        <Redirect exact from="/" to="/overview" />
        <PrivateRoute path="/overview" component={OverviewPage} />
        <PrivateRoute exact path="/holdings" component={HoldingsPage} />
        <PrivateRoute
          path="/holdings/fictive-portfolios/:portfolioId?"
          component={FictivePortfoliosPage}
        />
        <PrivateRoute
          path="/holdings/search/orders"
          component={OrderSearchPage}
        />
        <PrivateRoute
          path="/holdings/search/transactions"
          component={TransactionSearchPage}
        />
        <PrivateRoute
          path="/holdings/details/orders/:id"
          component={OrderDetailsPage}
        />
        <PrivateRoute
          path="/holdings/details/transactions/:orderId/:transactionId/:customerId/print"
          component={TransactionPrintPage}
          noWrapper
        />
        <PrivateRoute
          path="/holdings/details/transactions/:orderId/:transactionId"
          component={TransactionDetailsPage}
        />
        <PrivateRoute path="/lists/:instrumentType" component={ListsPage} />
        <PrivateRoute path="/analysis" component={AnalysisPage} />
        <PrivateRoute
          exact
          path="/instrument/:itemSector?"
          component={InstrumentPage}
        />
        <PrivateRoute
          path="/instrument/:itemSector/trades"
          component={TradesPage}
          onEnter={setPage}
        />
        <PrivateRoute exact path="/news" component={NewsPage} />
        <PrivateRoute path="/news/article/:referenceId" component={NewsPage} />
        <PrivateRoute path="/news/company/:item" component={NewsPage} />
        <PrivateRoute exact path="/tdn-news" component={TDNNewsPage} />
        <PrivateRoute path="/tdn-news/company/:item" component={TDNNewsPage} />
        <PrivateRoute path="/calendar" component={CalendarPage} />
        <PrivateRoute exact path="/alerts" component={AlertsPage} />
        <PrivateRoute
          path="/alerts/edit/:type/:id"
          component={EditAlertsPage}
        />
        <PrivateRoute
          exact
          path="/transactions/new"
          component={NewTransactionPage}
        />
        <PrivateRoute
          path="/transactions/new/:itemSector"
          component={NewTransactionPage}
        />
        <PrivateRoute exact path="/orders/new" component={NewOrderPage} />
        <PrivateRoute path="/orders/new/:itemSector" component={NewOrderPage} />
        <PrivateRoute path="/orders/edit/:id?" component={EditOrderPage} />
        <PrivateRoute path="/account/:vpsAccNo?" component={AccountPage} />
        <PrivateRoute path={['/faq', '/premium/faq']} component={FAQPage} />
        {/* Premium Account */}
        <PrivateRoute path="/premium/overview" component={PremiumDepotPage} />
        {/* Admin routes */}
        <Redirect exact from="/admin" to="/admin/login" />
        <AdminRoute exact path="/admin/users" component={UserListPage} />
        <AdminRoute path="/admin/users/new" component={UserNewPage} />
        <AdminRoute path="/admin/users/:userId/edit" component={UserEditPage} />
        <AdminRoute path="/admin/customers" component={CustomerSearchPage} />
        <Redirect exact from="/admin/audit" to="/admin/audit/report" />
        <AdminRoute path="/admin/audit/report" component={AuditReportPage} />
        <AdminRoute path="/admin/audit/search" component={AuditSearchPage} />
        <AdminRoute
          path="/admin/audit/statistics"
          component={AuditStatisticsPage}
        />
        <Redirect exact from="/admin/editor" to="/admin/editor/sniplets" />
        <AdminRoute path="/admin/editor/sniplets" component={SnipletsPage} />
        <AdminRoute path="/admin/editor/:id/edit" component={SnipletEditPage} />
        <AdminRoute
          path="/admin/editor/currency"
          component={CurrencyEditPage}
        />
        <AdminRoute exact path="/admin/reports" component={ReportListPage} />
        <AdminRoute
          path="/admin/reports/new/:type"
          component={FileUploadPage}
        />
        <AdminRoute
          path="/admin/reports/edit/company/:id"
          component={CompanyReportEditPage}
        />
        <AdminRoute
          path="/admin/takeover-in-progress"
          component={TakeoverInProgressPage}
        />
        {/* Open routes */}
        <Route path="/admin/login" component={AdminLoginPage} />
        <Route path="/admin/change-password" component={AdminChangePassword} />
        <Route path="/logged-out" component={LoggedOutMessagePage} />
        <Route path="/not-customer" component={NotCustomerMessagePage} />
        <Route path="*" component={NotFoundPage} />
      </Switch>
    </Router>
  </I18nProvider>
);
